/**
 * Viewer-side georeference record stored on `layer.style.georeference`.
 *
 * Used only when a raster has no earth CRS. Properly georeferenced layers
 * never receive this block, so their existing placement is left alone.
 */

export type LonLat = readonly [number, number];

export type GeoreferenceStatus =
  | 'pending'
  | 'fake-coordinates'
  | 'rectifying'
  | 'applied';

export interface GeoreferenceControlPoint {
  readonly id: string;
  readonly source: LonLat;
  readonly target: LonLat;
}

/** X = a·x + b·y + c,  Y = d·x + e·y + f */
export interface AffineTransform {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

export type GeoreferenceCorners = readonly [LonLat, LonLat, LonLat, LonLat];

export interface LayerGeoreference {
  readonly status: GeoreferenceStatus;
  readonly controlPoints?: readonly GeoreferenceControlPoint[];
  readonly affine?: AffineTransform | null;
  readonly corners?: GeoreferenceCorners;
  readonly bbox?: readonly [number, number, number, number];
  readonly sourceCrs?: string;
  readonly targetCrs?: string;
  readonly rmse?: number | null;
  readonly previewUrl?: string | null;
}

export const GEOREFERENCE_STYLE_KEY = 'georeference';

function isLonLat(value: unknown): value is LonLat {
  return (
    Array.isArray(value)
    && value.length >= 2
    && Number.isFinite(value[0])
    && Number.isFinite(value[1])
  );
}

function isStatus(value: unknown): value is GeoreferenceStatus {
  return (
    value === 'pending'
    || value === 'fake-coordinates'
    || value === 'rectifying'
    || value === 'applied'
  );
}

export function readGeoreference(
  layer: { readonly style?: Record<string, unknown> | null } | null | undefined,
): LayerGeoreference | null {
  const raw = layer?.style?.[GEOREFERENCE_STYLE_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  if (!isStatus(record.status)) return null;

  const controlPoints: GeoreferenceControlPoint[] = [];
  if (Array.isArray(record.controlPoints)) {
    for (const entry of record.controlPoints) {
      if (!entry || typeof entry !== 'object') continue;
      const point = entry as Record<string, unknown>;
      if (typeof point.id !== 'string' || !isLonLat(point.source) || !isLonLat(point.target)) {
        continue;
      }
      controlPoints.push({
        id: point.id,
        source: [point.source[0], point.source[1]],
        target: [point.target[0], point.target[1]],
      });
    }
  }

  const affineRaw = record.affine;
  let affine: AffineTransform | null = null;
  if (affineRaw && typeof affineRaw === 'object') {
    const a = affineRaw as Record<string, unknown>;
    if (
      [a.a, a.b, a.c, a.d, a.e, a.f].every(
        (value) => typeof value === 'number' && Number.isFinite(value),
      )
    ) {
      affine = {
        a: a.a as number,
        b: a.b as number,
        c: a.c as number,
        d: a.d as number,
        e: a.e as number,
        f: a.f as number,
      };
    }
  }

  const cornersRaw = record.corners;
  let corners: GeoreferenceCorners | undefined;
  if (Array.isArray(cornersRaw) && cornersRaw.length === 4 && cornersRaw.every(isLonLat)) {
    corners = [
      [cornersRaw[0][0], cornersRaw[0][1]],
      [cornersRaw[1][0], cornersRaw[1][1]],
      [cornersRaw[2][0], cornersRaw[2][1]],
      [cornersRaw[3][0], cornersRaw[3][1]],
    ];
  }

  const bboxRaw = record.bbox;
  const bbox =
    Array.isArray(bboxRaw)
    && bboxRaw.length >= 4
    && bboxRaw.slice(0, 4).every((value) => typeof value === 'number' && Number.isFinite(value))
      ? ([bboxRaw[0], bboxRaw[1], bboxRaw[2], bboxRaw[3]] as const)
      : undefined;

  return {
    status: record.status,
    controlPoints: controlPoints.length > 0 ? controlPoints : undefined,
    affine,
    corners,
    bbox,
    sourceCrs: typeof record.sourceCrs === 'string' ? record.sourceCrs : undefined,
    targetCrs: typeof record.targetCrs === 'string' ? record.targetCrs : undefined,
    rmse: typeof record.rmse === 'number' && Number.isFinite(record.rmse) ? record.rmse : null,
    previewUrl: typeof record.previewUrl === 'string' ? record.previewUrl : null,
  };
}

export function bboxFromCorners(
  corners: GeoreferenceCorners | undefined,
): [number, number, number, number] | null {
  if (!corners) return null;
  const xs = corners.map((corner) => corner[0]);
  const ys = corners.map((corner) => corner[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  if (!(maxX > minX) || !(maxY > minY)) return null;
  return [minX, minY, maxX, maxY];
}
